<script setup lang="ts">
import { useErhlLoanAppProcess } from '@/helpers/useErhlLoanAppProcess';
import api from '@/api';
import { useApplicationApi } from '@/helpers/useApplicationApi';
import { INVALID_JWT_ERROR, useSessionExpiry } from '@/helpers/useSessionExpiry';
import { computed, ref } from 'vue';
import { erhlExitStrategySchema } from '@/validation';
import { useForm } from 'vee-validate';
import {
  BorrowerType,
  ErhlExitStrategyOptions,
  ErhlExitStrategyValue,
  type ErhlExitStrategyEnum,
} from '@/helpers/const';
import {
  hasAnyAssets,
  hasInvestmentProperty,
  hasOwnerOccupiedProperty,
  hasSuperannuationAsset,
} from '@/helpers/typeGuards';

const { loanApp, state, saveAndGoNext, goBack } = useErhlLoanAppProcess();
const { saveApplicationToDraft } = useApplicationApi();
const { flagSessionExpired } = useSessionExpiry();
const isLoading = ref(false);
const validationError = ref('');

interface ErhlExitStrategyFormValues {
  exitStrategy?: ErhlExitStrategyEnum;
  exitStrategyDetails?: string;
}

const { handleSubmit: validateSubmit } = useForm<ErhlExitStrategyFormValues>({
  validationSchema: erhlExitStrategySchema,
  initialValues: {
    exitStrategy: loanApp.value.exitStrategy as ErhlExitStrategyEnum | undefined,
    exitStrategyDetails: loanApp.value.exitStrategyDetails ?? '',
  },
  validateOnMount: false,
});

const hasNonIndividualBorrower = computed(
  () =>
    loanApp.value.borrowerGroups?.some((g) => g.type === BorrowerType.COMPANY || g.type === BorrowerType.TRUST) ??
    false,
);

/**
 * Returns a question string tailored to borrower composition.
 * @param singular - Wording for a single individual applicant
 * @param plural - Wording for multiple individual applicants
 * @param entity - Wording for a Company or Trust borrower
 */
const getBorrowerQuestion = (singular: string, plural: string, entity: string): string => {
  if (hasNonIndividualBorrower.value) return entity;
  return loanApp.value.applicants.length > 1 ? plural : singular;
};

const repaymentQuestion = computed(() =>
  getBorrowerQuestion(
    'How does the applicant plan to repay the loan at the end of the term?',
    'How do the applicants plan to repay the loan at the end of the term?',
    'How does the borrower plan to repay the loan at the end of the term?',
  ),
);

const repaymentDetailsLabel = computed(() =>
  getBorrowerQuestion(
    "Please provide further details on the applicant's repayment strategy.",
    'Please provide further details on the applicants repayment strategy.',
    "Please provide further details on the borrower's repayment strategy.",
  ),
);

/** Validates and saves the exit strategy, then advances to the next step. */
const handleSubmit = validateSubmit(async (validated) => {
  if (
    validated.exitStrategy === ErhlExitStrategyValue.DOWNSIZING_PRIMARY_RESIDENCE &&
    !hasOwnerOccupiedProperty(loanApp.value)
  ) {
    validationError.value =
      "No owner occupied properties have been listed. Add the relevant property in Property details (if it's a security property), or in Assets & Liabilities (if it's not).";
    return;
  }

  if (
    validated.exitStrategy === ErhlExitStrategyValue.SALE_OF_INVESTMENT_PROPERTY &&
    !hasInvestmentProperty(loanApp.value)
  ) {
    validationError.value =
      "No investment properties have been listed. Add the relevant property in Property details (if it's a security property), or in Assets & Liabilities (if it's not).";
    return;
  }

  if (
    validated.exitStrategy === ErhlExitStrategyValue.SUPERANNUATION_LUMP_SUM_WITHDRAWAL &&
    !hasSuperannuationAsset(loanApp.value)
  ) {
    validationError.value = 'No superannuation fund has been listed. Add the relevant fund in Assets & Liabilities.';
    return;
  }

  if (validated.exitStrategy === ErhlExitStrategyValue.SALE_OF_SHARES_OR_OTHER_ASSETS && !hasAnyAssets(loanApp.value)) {
    validationError.value =
      'No shares or other assets have been listed. Add the relevant asset in Assets & Liabilities.';
    return;
  }

  validationError.value = '';

  isLoading.value = true;
  try {
    const updates = {
      exitStrategy: validated.exitStrategy,
      exitStrategyDetails: validated.exitStrategyDetails,
    };

    let applicationDraftId = loanApp.value.applicationDraftId;
    if (loanApp.value.brokerId) {
      const savedDraftId = await saveApplicationToDraft(state.value.context, {
        ...loanApp.value,
        ...updates,
      });
      applicationDraftId = savedDraftId ?? applicationDraftId;
    }
    saveAndGoNext({ ...updates, applicationDraftId });
  } catch (error) {
    if (api.getErrorMsg(error) === INVALID_JWT_ERROR) {
      flagSessionExpired();
      return;
    }
    console.error('Failed to save exit strategy:', error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div class="w-full">
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        <BxHighlight>Exit strategy</BxHighlight>
      </h2>
    </div>
    <form @submit="handleSubmit">
      <BxCard>
        <p class="mb-4 text-base font-medium text-gray-900">{{ repaymentQuestion }}</p>
        <BxField v-slot="{ field }" name="exitStrategy">
          <BxSelect
            v-bind="field"
            id="exit-strategy"
            data-test-id="exit-strategy"
            label="Repayment strategy"
            label-type="floating"
            placeholder="Select exit strategy..."
            :list-items="ErhlExitStrategyOptions"
            :truncate="false"
            class="form-control"
          />
        </BxField>
        <BxField v-slot="{ field }" name="exitStrategyDetails" class="mt-4">
          <BxTextarea
            v-bind="field"
            id="exit-strategy-details"
            data-test-id="exit-strategy-details"
            :label="repaymentDetailsLabel"
            label-type="floating"
            class="form-control"
          />
        </BxField>
      </BxCard>
      <BxErrorPanel v-if="validationError" class="mt-6">
        {{ validationError }}
      </BxErrorPanel>
      <div class="mt-6">
        <BxButton id="exit-strategy-continue" variant="primary" :loading="isLoading" @click="handleSubmit()">
          Save and continue
        </BxButton>
        <BxButton variant="link" class="ml-4" @click="goBack()">Back</BxButton>
      </div>
    </form>
  </div>
</template>
